import { motion } from 'framer-motion'
import { Shield, Lock, Eye, Server, UserCheck, FileText } from 'lucide-react'
import SEO from '../components/SEO'
import { useIntl } from 'react-intl'
import { useLanguage } from '../contexts/LanguageContext'
import { messages } from '../i18n'

const Privacy = () => {
  const intl = useIntl()
  const { locale } = useLanguage()
  const t = (id: string, options?: any) => intl.formatMessage({ id }, options)
  
  // 获取数组类型的翻译
  const getArrayTranslation = (id: string): string[] => {
    const flat = messages[locale]
    const value = flat?.[id]
    return Array.isArray(value) ? value : []
  }

  const privacyFeatures = [
    {
      icon: <Lock className="h-6 w-6" />,
      title: t('privacy.features.localProcessing.title'),
      description: t('privacy.features.localProcessing.description')
    },
    {
      icon: <Server className="h-6 w-6" />,
      title: t('privacy.features.noServerUpload.title'),
      description: t('privacy.features.noServerUpload.description')
    },
    {
      icon: <Eye className="h-6 w-6" />,
      title: t('privacy.features.noTracking.title'),
      description: t('privacy.features.noTracking.description')
    },
    {
      icon: <UserCheck className="h-6 w-6" />,
      title: t('privacy.features.noRegistration.title'),
      description: t('privacy.features.noRegistration.description')
    }
  ]

  return (
    <>
      <SEO
        title={t('privacy.title')}
        description={t('privacy.description')}
        keywords={t('privacy.keywords')}
      />
      
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="min-h-screen pt-20"
      >
        {/* Header Section */}
        <section className="gradient-bg py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 text-primary-600 rounded-full mb-6">
                <Shield className="h-8 w-8" />
              </div>
              <h1 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-6">
                {t('privacy.mainTitle')}
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t('privacy.mainDescription')}
              </p>
              <p className="text-sm text-gray-500 mt-4">
                {t('privacy.lastUpdatedText')} {t('privacy.lastUpdated')}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Privacy Features */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-6">
                {t('privacy.privacyByDesign.title')}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t('privacy.privacyByDesign.description')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {privacyFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-100 text-primary-600 rounded-lg mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Detailed Privacy Policy */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="prose prose-lg max-w-none"
            >
              <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <FileText className="h-6 w-6 mr-2 text-primary-600" />
                    {t('privacy.sections.informationWeCollect.title')}
                  </h2>
                  <p className="text-gray-700 mb-4">
                    {t('privacy.sections.informationWeCollect.description')}
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-2">
                    {getArrayTranslation('privacy.sections.informationWeCollect.items').map((item, index) => (
                      <li key={index} dangerouslySetInnerHTML={{ __html: item }} />
                    ))}
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    {t('privacy.sections.howWeProtectImages.title')}
                  </h2>
                  <p className="text-gray-700 mb-4">
                    {t('privacy.sections.howWeProtectImages.description')}
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-2">
                    {getArrayTranslation('privacy.sections.howWeProtectImages.items').map((item, index) => (
                      <li key={index} dangerouslySetInnerHTML={{ __html: item }} />
                    ))}
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    {t('privacy.sections.cookiesAndTracking.title')}
                  </h2>
                  <p className="text-gray-700 mb-4">
                    {t('privacy.sections.cookiesAndTracking.description')}
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-2">
                    {getArrayTranslation('privacy.sections.cookiesAndTracking.items').map((item, index) => (
                      <li key={index} dangerouslySetInnerHTML={{ __html: item }} />
                    ))}
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    {t('privacy.sections.dataSharingAndDisclosure.title')}
                  </h2>
                  <p className="text-gray-700 mb-4">
                    {t('privacy.sections.dataSharingAndDisclosure.description')}
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-2">
                    {getArrayTranslation('privacy.sections.dataSharingAndDisclosure.items').map((item, index) => (
                      <li key={index} dangerouslySetInnerHTML={{ __html: item }} />
                    ))}
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    {t('privacy.sections.yourRightsAndChoices.title')}
                  </h2>
                  <p className="text-gray-700 mb-4">
                    {t('privacy.sections.yourRightsAndChoices.description')}
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-2">
                    {getArrayTranslation('privacy.sections.yourRightsAndChoices.items').map((item, index) => (
                      <li key={index} dangerouslySetInnerHTML={{ __html: item }} />
                    ))}
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    {t('privacy.sections.securityMeasures.title')}
                  </h2>
                  <p className="text-gray-700 mb-4">
                    {t('privacy.sections.securityMeasures.description')}
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-2">
                    {getArrayTranslation('privacy.sections.securityMeasures.items').map((item, index) => (
                      <li key={index} dangerouslySetInnerHTML={{ __html: item }} />
                    ))}
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    {t('privacy.sections.childrensPrivacy.title')}
                  </h2>
                  <p className="text-gray-700">
                    {t('privacy.sections.childrensPrivacy.description')}
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    {t('privacy.sections.changesToThisPolicy.title')}
                  </h2>
                  <p className="text-gray-700 mb-4">
                    {t('privacy.sections.changesToThisPolicy.description')}
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-2">
                    {getArrayTranslation('privacy.sections.changesToThisPolicy.items').map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    {t('privacy.sections.contactInformation.title')}
                  </h2>
                  <p className="text-gray-700">
                    {t('privacy.sections.contactInformation.description')}
                  </p>
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <p className="text-gray-700">
                      <strong>{t('privacy.sections.contactInformation.email')}:</strong> contact@avatarcropper.org
                    </p>
                    <p className="text-gray-700 mt-2">
                      {t('privacy.sections.contactInformation.responseTime')}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </motion.div>
    </>
  )
}

export default Privacy
